import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, finalize, map, of, shareReplay, switchMap, timer } from 'rxjs';
import { jwtDecode } from 'jwt-decode';
import { APP_ROUTES } from '../constants/app-routes';
import { API_PATHS } from '../constants/api-paths';
import { HTTP_HEADER_NAMES } from '../constants/http-headers';
import { ApiResponse, AuthResponse, JwtPayload, UserProfile } from '../models/auth.model';
import { AppConfigService } from './app-config.service';

/**
 * Session EasyLoc : stub local ou JWT Launcher (refresh cookie, Bearer, logout).
 */
@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {

  private readonly LEGACY_SESSION_KEY = 'authenticatedUser';
  private readonly REFRESH_BEFORE_EXPIRY_MS = 5 * 60 * 1000;
  private readonly REFRESH_RETRY_DELAY_MS = 400;

  private accessToken: string | null = null;
  private userProfile: UserProfile | null = null;
  private refreshRequest$: Observable<boolean> | null = null;
  private refreshTimerId: ReturnType<typeof setTimeout> | null = null;
  private sessionBootstrapCompleted = false;

  constructor(
    private readonly http: HttpClient,
    private readonly appConfig: AppConfigService
  ) {
    this.clearLegacyStorage();
  }

  /**
   * Bootstrap session au démarrage : stub, session déjà valide, ou refresh cookie.
   * @returns {@code true} si une session utilisable est en place
   */
  initializeSession(): Observable<boolean> {
    if (this.isStubAuth()) {
      this.applyStubSession();
      this.sessionBootstrapCompleted = true;
      return of(true);
    }
    if (this.isUserLoggedIn()) {
      this.sessionBootstrapCompleted = true;
      this.scheduleProactiveRefresh();
      return of(true);
    }
    return this.refreshAccessToken().pipe(
      map(result => {
        this.sessionBootstrapCompleted = true;
        return result;
      })
    );
  }

  /** True une fois le bootstrap {@link initializeSession} terminé. */
  hasSessionBootstrapCompleted(): boolean {
    return this.sessionBootstrapCompleted;
  }

  /**
   * Renouvelle l'access token via le refresh cookie Launcher
   * (déduplique les appels concurrents).
   */
  refreshAccessToken(): Observable<boolean> {
    if (this.isStubAuth()) {
      this.applyStubSession();
      return of(true);
    }
    if (this.refreshRequest$) {
      return this.refreshRequest$;
    }

    this.refreshRequest$ = this.requestRefresh(false).pipe(
      switchMap(success => {
        if (success) {
          return of(true);
        }
        return timer(this.REFRESH_RETRY_DELAY_MS).pipe(
          switchMap(() => this.requestRefresh(true))
        );
      }),
      finalize(() => {
        this.refreshRequest$ = null;
      }),
      shareReplay(1)
    );

    return this.refreshRequest$;
  }

  /** Efface la session locale sans révoquer le refresh token partagé (SSO). */
  abandonSession(): void {
    this.clearRefreshTimer();
    this.clearSession();
  }

  /** Révoque le refresh côté Launcher puis efface la session locale. */
  logout(): Observable<void> {
    this.abandonSession();
    if (this.isStubAuth()) {
      this.applyStubSession();
      return of(undefined);
    }
    const url = `${this.appConfig.getLauncherApiUrl()}${API_PATHS.authLogout}`;
    return this.http.post(url, {}, { withCredentials: true }).pipe(
      catchError(() => of(null)),
      map(() => undefined)
    );
  }

  /** Navigue vers le front Launcher. */
  openLauncher(): void {
    const url = this.appConfig.getLauncherFrontUrl();
    if (url) {
      window.location.assign(url);
    }
  }

  /** Redirige vers la page de connexion Launcher avec {@code returnUrl}. */
  redirectToLauncherLogin(path: string = APP_ROUTES.accueil): void {
    if (this.isStubAuth()) {
      return;
    }
    const launcherFront = this.appConfig.getLauncherFrontUrl();
    let returnUrl: string;
    if (path.startsWith('http://') || path.startsWith('https://')) {
      returnUrl = path;
    } else {
      const normalizedPath = path.startsWith('/') ? path : `/${path}`;
      returnUrl = `${window.location.origin}${normalizedPath}`;
    }
    window.location.href = `${launcherFront}/connexion?returnUrl=${encodeURIComponent(returnUrl)}`;
  }

  /** Access token JWT courant (Bearer), ou {@code null}. */
  getAuthToken(): string | null {
    return this.accessToken;
  }

  /** True si le JWT est présent et non expiré. */
  isTokenValid(): boolean {
    const token = this.getAuthToken();
    if (!token) {
      return false;
    }

    try {
      const decodedToken = jwtDecode<JwtPayload>(token);
      return this.checkTokenValidity(decodedToken);
    } catch {
      return false;
    }
  }

  /** True si l'utilisateur a une session active (stub ou JWT valide). */
  isUserLoggedIn(): boolean {
    if (this.isStubAuth()) {
      return this.userProfile !== null;
    }
    return this.accessToken !== null && this.isTokenValid();
  }

  /** Délègue à {@link AppConfigService.isStubAuth}. */
  isStubAuth(): boolean {
    return this.appConfig.isStubAuth();
  }

  /** Identifiant technique ({@code sub}) du profil courant. */
  getLoggedInUserName(): string | null {
    return this.userProfile?.sub ?? null;
  }

  getUserFirstName(): string {
    return this.userProfile?.prenom ?? '';
  }

  getUserLastName(): string {
    return this.userProfile?.nom ?? '';
  }

  /** Prénom + nom, ou la partie disponible. */
  getUserFullName(): string {
    const firstName = this.getUserFirstName().trim();
    const lastName = this.getUserLastName().trim();

    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    }
    if (firstName) {
      return firstName;
    }
    if (lastName) {
      return lastName;
    }

    return '';
  }

  /** Rôle applicatif du profil courant. */
  getUserRole(): string {
    return this.userProfile?.role || '';
  }

  /** Id d'organisation multi-tenant, ou {@code null}. */
  getUserOrganisationId(): number | null {
    return this.userProfile?.orgId ?? null;
  }

  private applyStubSession(): void {
    const stubUser = this.appConfig.getStubUser();
    if (!stubUser) {
      return;
    }
    this.userProfile = stubUser;
    this.accessToken = this.createStubAccessToken(stubUser);
  }

  private createStubAccessToken(user: UserProfile): string {
    const encode = (value: unknown): string =>
      this.toBase64Url(JSON.stringify(value));
    const payload = {
      sub: user.sub,
      nom: user.nom,
      prenom: user.prenom,
      role: user.role,
      org_id: user.orgId,
      org_libelle: user.orgLibelle,
      exp: Math.floor(Date.now() / 1000) + 365 * 24 * 60 * 60
    };
    return `${encode({ alg: 'none', typ: 'JWT' })}.${encode(payload)}.stub`;
  }

  /** Base64 URL-safe sans l'API dépréciée {@code btoa(string)}. */
  private toBase64Url(value: string): string {
    const bytes = new TextEncoder().encode(value);
    const alphabet =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
    let output = '';
    for (let i = 0; i < bytes.length; i += 3) {
      const a = bytes[i];
      const b = i + 1 < bytes.length ? bytes[i + 1] : 0;
      const c = i + 2 < bytes.length ? bytes[i + 2] : 0;
      const bitmap = (a << 16) | (b << 8) | c;
      output += alphabet.charAt((bitmap >> 18) & 63);
      output += alphabet.charAt((bitmap >> 12) & 63);
      if (i + 1 < bytes.length) {
        output += alphabet.charAt((bitmap >> 6) & 63);
      }
      if (i + 2 < bytes.length) {
        output += alphabet.charAt(bitmap & 63);
      }
    }
    return output;
  }

  private requestRefresh(isRetry: boolean): Observable<boolean> {
    const url = `${this.appConfig.getLauncherApiUrl()}${API_PATHS.authRefresh}`;
    return this.http.post<ApiResponse<AuthResponse>>(url, {}, {
      withCredentials: true,
      headers: isRetry ? { [HTTP_HEADER_NAMES.authRefreshRetry]: 'true' } : undefined
    }).pipe(
      map(response => this.applyAuthResponse(response)),
      catchError(() => of(false))
    );
  }

  private applyAuthResponse(response: ApiResponse<AuthResponse> | null | undefined): boolean {
    if (!response?.success || !response.data?.accessToken) {
      return false;
    }

    try {
      const decodedToken = jwtDecode<JwtPayload>(response.data.accessToken);
      if (!this.checkTokenValidity(decodedToken)) {
        return false;
      }

      this.accessToken = response.data.accessToken;
      this.userProfile = this.mapUserProfile(response.data);
      this.scheduleProactiveRefresh();
      return true;
    } catch {
      return false;
    }
  }

  private scheduleProactiveRefresh(): void {
    this.clearRefreshTimer();

    const token = this.getAuthToken();
    if (!token) {
      return;
    }

    try {
      const decodedToken = jwtDecode<JwtPayload>(token);
      if (!decodedToken.exp) {
        return;
      }

      const refreshAt = decodedToken.exp * 1000 - this.REFRESH_BEFORE_EXPIRY_MS;
      const delay = refreshAt - Date.now();

      if (delay <= 0) {
        this.refreshAccessToken().subscribe();
        return;
      }

      this.refreshTimerId = setTimeout(() => {
        this.refreshAccessToken().subscribe(refreshed => {
          if (!refreshed && this.isUserLoggedIn()) {
            setTimeout(() => this.refreshAccessToken().subscribe(), this.REFRESH_RETRY_DELAY_MS);
          }
        });
      }, delay);
    } catch {
      // ignore
    }
  }

  private clearRefreshTimer(): void {
    if (this.refreshTimerId !== null) {
      clearTimeout(this.refreshTimerId);
      this.refreshTimerId = null;
    }
  }

  private mapUserProfile(authResponse: AuthResponse): UserProfile {
    const user = authResponse.user;
    let nom = user.nom?.trim() ?? '';
    let prenom = user.prenom?.trim() ?? '';

    if (!nom || !prenom) {
      try {
        const claims = jwtDecode<JwtPayload>(authResponse.accessToken);
        if (!nom) {
          nom = claims.nom?.trim() ?? '';
        }
        if (!prenom) {
          prenom = claims.prenom?.trim() ?? '';
        }
      } catch {
        // ignore
      }
    }

    return {
      sub: user.sub,
      nom,
      prenom,
      role: user.role ?? 'user',
      orgId: user.orgId,
      orgLibelle: user.orgLibelle
    };
  }

  private checkTokenValidity(decodedToken: JwtPayload): boolean {
    if (!decodedToken.exp) {
      return true;
    }

    return Date.now() < decodedToken.exp * 1000;
  }

  private clearSession(): void {
    this.accessToken = null;
    this.userProfile = null;
    this.clearLegacyStorage();
  }

  private clearLegacyStorage(): void {
    sessionStorage.removeItem(this.LEGACY_SESSION_KEY);
    localStorage.removeItem(this.LEGACY_SESSION_KEY);
  }
}
